import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { motion } from 'framer-motion';

export default function ObjectionSection() {
  const { language } = useLanguage();

  const faqs = language === 'fr' ? [
    {
      question: 'Qu\'est-ce qu\'OFA France ?',
      answer: 'OFA France est un parcours bilingue et international proposé par OFA Lycée pour les élèves scolarisés en France, de la Grande Section à la Terminale. Les élèves restent inscrits dans leur établissement en France tout en développant un véritable parcours anglophone et international avec OFA Lycée.',
    },
    {
      question: 'Mon enfant reste-t-il inscrit dans son école en France ?',
      answer: 'Oui. OFA France fonctionne en complément de la scolarité principale de l\'élève. Notre objectif est d\'apporter une dimension bilingue, internationale et académique forte sans rupture de parcours.',
    },
    {
      question: 'À partir de quel âge peut-on rejoindre OFA France ?',
      answer: 'Les élèves peuvent rejoindre OFA France dès la Grande Section et jusqu\'à la Terminale.',
    },
    {
      question: 'Les cours sont-ils entièrement en ligne ?',
      answer: 'Oui. Les cours ont lieu en direct, en petits groupes, avec des enseignants natifs et expérimentés.',
    },
    {
      question: 'Faut-il déjà être bilingue en anglais ?',
      answer: 'Non, pas forcément. Cela dépend du parcours choisi et du niveau de l\'élève. Certains programmes sont accessibles avec un niveau intermédiaire, tandis que d\'autres demandent davantage d\'aisance en anglais.',
    },
    {
      question: 'Combien d\'heures par semaine faut-il prévoir ?',
      answer: 'Les élèves suivent 1h30 de cours en direct par semaine (1 cours) ou 3h par semaine (2 cours) sur 32 semaines, auxquelles s\'ajoutent du travail personnel. L\'organisation est pensée pour rester compatible avec une scolarité en France tout en permettant une progression régulière en anglais et dans les compétences académiques internationales.',
    },
    {
      question: 'Quel est l\'objectif du programme ?',
      answer: 'Développer un véritable bilinguisme académique et culturel sur le long terme, tout en ouvrant davantage de possibilités pour les études supérieures en France et à l\'international.',
    },
    {
      question: 'Quelle est la différence entre OFA France et des cours d\'anglais classiques ?',
      answer: 'OFA France ne propose pas uniquement de l\'anglais : les élèves développent des compétences académiques, culturelles et méthodologiques dans une logique de parcours bilingue durable.',
    },
    {
      question: 'Les élèves peuvent-ils préparer un diplôme américain ?',
      answer: 'Oui. Selon le parcours choisi, certains élèves peuvent préparer un diplôme américain accrédité en parallèle de leur scolarité française.',
    },
    {
      question: 'Quelle est la taille des groupes ?',
      answer: 'Les groupes sont volontairement limités à 4 à 8 élèves maximum afin de favoriser la participation, les échanges et un accompagnement individualisé.',
    },
    {
      question: 'Qui sont les enseignants ?',
      answer: 'Nos enseignants sont natifs, certifiés ou expérimentés dans l\'enseignement bilingue et international.',
    },
    {
      question: 'Comment se déroule l\'admission ?',
      answer: 'L\'admission se fait sur dossier, évaluation et entretien afin de vérifier que le parcours correspond bien au profil, au niveau et aux objectifs de l\'élève.',
    },
  ] : [
    {
      question: 'What is OFA France?',
      answer: 'OFA France is a bilingual and international pathway offered by OFA Lycée for students enrolled in France, from Kindergarten to 12th grade. Students remain enrolled in their French school while developing a genuine English-speaking and international pathway with OFA Lycée.',
    },
    {
      question: 'Does my child stay enrolled in their French school?',
      answer: 'Yes. OFA France works as a complement to the student\'s main schooling. Our goal is to bring a strong bilingual, international and academic dimension without disrupting their education.',
    },
    {
      question: 'What is the minimum age to join OFA France?',
      answer: 'Students can join OFA France from Kindergarten through 12th grade.',
    },
    {
      question: 'Are courses entirely online?',
      answer: 'Yes. Classes are held live, in small groups, with native and experienced teachers.',
    },
    {
      question: 'Does my child need to be bilingual already?',
      answer: 'Not necessarily. It depends on the chosen track and the student\'s level. Some programs are accessible with an intermediate level, while others require greater fluency in English.',
    },
    {
      question: 'How many hours per week should we plan for?',
      answer: 'Students attend 1.5 hours of live classes per week (1 course) or 3 hours per week (2 courses) over 32 weeks, plus personal work. The schedule is designed to remain compatible with French schooling while enabling regular progress in English and international academic skills.',
    },
    {
      question: 'What is the goal of the program?',
      answer: 'To develop genuine long-term academic and cultural bilingualism, while opening more opportunities for higher education in France and internationally.',
    },
    {
      question: 'What is the difference between OFA France and regular English classes?',
      answer: 'OFA France doesn\'t just offer English: students develop academic, cultural and methodological skills as part of a lasting bilingual pathway.',
    },
    {
      question: 'Can students prepare an American diploma?',
      answer: 'Yes. Depending on the chosen track, some students can prepare an accredited American diploma alongside their French schooling.',
    },
    {
      question: 'What is the class size?',
      answer: 'Groups are deliberately limited to 4 to 8 students maximum to encourage participation, exchanges and personalized support.',
    },
    {
      question: 'Who are the teachers?',
      answer: 'Our teachers are native speakers, certified or experienced in bilingual and international education.',
    },
    {
      question: 'How does the admission process work?',
      answer: 'Admission is based on application, assessment and interview to ensure the track matches the student\'s profile, level and goals.',
    },
  ];

  return (
    <section
      data-testid="objection-section"
      className="bg-white py-24 md:py-32"
    >
      {/* FAQ Schema.org structured data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(faq => ({
          "@type": "Question",
          "name": faq.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": faq.answer
          }
        }))
      })}} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-3xl sm:text-4xl lg:text-5xl font-light text-[#14274B] leading-tight tracking-tight"
            style={{ fontFamily: 'Gabarito, sans-serif' }}
          >
            {language === 'fr' ? 'Questions Fréquentes' : 'Frequently Asked Questions'}
          </motion.h2>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                data-testid={`faq-item-${index}`}
                className="border-b border-[#14274B]/10"
              >
                <AccordionTrigger
                  className="text-left py-6 hover:no-underline group"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  <span className="text-lg font-medium text-[#14274B] group-hover:text-[#2D78F6] transition-colors">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent
                  className="text-[#475569] leading-relaxed pb-6"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          {/* CTA to full FAQ */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="mt-10 text-center"
          >
            <Link
              to="/faq"
              data-testid="faq-see-all"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#2D78F6] text-white font-medium hover:bg-[#14274B] transition-colors"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              {language === 'fr' ? 'Voir toutes les questions (22)' : 'See all questions (22)'}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
